// Reader-side rendering for blog posts, shared by the composer's live
// preview and the published list on the same page. Extracted so the preview
// cannot drift from what actually renders - they are literally the same code.

import React from "react";
import { API_BASE } from "./api";

// Block media src is stored as a relative API path; prepend API_BASE for an
// absolute browser URL. blob: (a pending upload, pre-save) passes through.
export function mediaUrl(src) {
    if (!src) return src;
    if (src.startsWith("blob:")) return src;
    if (/^https?:\/\//i.test(src)) return src;
    return `${API_BASE}${src}`;
}

export function fmtDate(s) {
    if (!s) return "";
    try {
        const d = new Date(s);
        return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
    } catch { return s; }
}

// Media sizing - ONE percentage sizes the wrapper and the media scales
// proportionally inside it; older posts carry the legacy
// small/medium/large/full string instead.
const LEGACY_WIDTH_PCT = { small: 25, medium: 50, large: 75, full: 100 };
// Smallest media width the slider allows.
export const MEDIA_PCT_MIN = 10;

export const mediaWidthPct = (b) => {
    const n = Number(b?.widthPct);
    if (Number.isFinite(n)) return Math.min(100, Math.max(MEDIA_PCT_MIN, n));
    return LEGACY_WIDTH_PCT[b?.width] ?? 100;
};
export const mediaSizeStyle = (b) => ({ width: `${mediaWidthPct(b)}%` });

function applyQuillStyles(html) {
    if (!html) return html;

    const sizeMap = {
        "ql-size-small": "font-size:0.75em",
        "ql-size-large": "font-size:1.5em",
        "ql-size-huge": "font-size:2.5em",
    };
    const alignMap = {
        "ql-align-center": "text-align:center",
        "ql-align-right": "text-align:right",
        "ql-align-justify": "text-align:justify",
    };
    const indentMap = {
        "ql-indent-1": "padding-left:3em",
        "ql-indent-2": "padding-left:6em",
        "ql-indent-3": "padding-left:9em",
    };
    const allMap = { ...sizeMap, ...alignMap, ...indentMap };

    // Match any opening tag and rewrite it
    return html.replace(/<([a-zA-Z][a-zA-Z0-9]*)((?:\s[^>]*)?)>/g, (fullTag, tag, attrs) => {
        // Find which ql- classes are present
        const classMatch = attrs.match(/class="([^"]*)"/);
        if (!classMatch) return fullTag;

        const classes = classMatch[1].split(/\s+/);
        const extraStyles = classes
            .filter(c => allMap[c])
            .map(c => allMap[c])
            .join(";");

        if (!extraStyles) return fullTag;

        // Merge into existing style= or add new one
        if (/style="([^"]*)"/.test(attrs)) {
            const newAttrs = attrs.replace(/style="([^"]*)"/, (_, s) => `style="${s};${extraStyles}"`);
            return `<${tag}${newAttrs}>`;
        } else {
            const newAttrs = attrs + ` style="${extraStyles}"`;
            return `<${tag}${newAttrs}>`;
        }
    });
}

// Render a single block. Recurses into row.cols for the row block type.
// Text + heading blocks store rich HTML so we use dangerouslySetInnerHTML
// - the source is admin-authored content, no user input here.

export function RenderBlock({ block, inRow = false }) {
    if (!block) return inRow ? <div className="dnews-bp-row-empty" /> : null;

    if (block.type === "heading" || block.type === "text" || block.type === "paragraph") {
        const html = applyQuillStyles(block.html || block.text || "");
        return (
            <div
                className={`dnews-bp-rich ${block.type === "heading" ? "is-heading" : ""}`}
                dangerouslySetInnerHTML={{ __html: html }}
            />
        );
    }

    if (block.type === "image") {
        return (
            <div className={`dnews-blog-media-block align-${block.align || "full"}`} style={mediaSizeStyle(block)}>
                <img
                    src={mediaUrl(block.src)}
                    alt={block.alt || ""}
                    className="dnews-blog-media-img"
                />
            </div>
        );
    }

    if (block.type === "video") {
        return (
            <div className={`dnews-blog-media-block align-${block.align || "full"}`} style={mediaSizeStyle(block)}>
                <video
                    src={mediaUrl(block.src)}
                    controls
                    className="dnews-blog-media-video"
                />
            </div>
        );
    }

    if (block.type === "divider") {
        return <hr className="dnews-blog-divider" />;
    }

    if (block.type === "row") {
        const cols = Array.isArray(block.cols) ? block.cols : [null, null];
        return (
            <div className="dnews-blog-row">
                {cols.map((c, i) => (
                    <div key={i} className="dnews-bp-row-col">
                        <RenderBlock block={c} inRow />
                    </div>
                ))}
            </div>
        );
    }

    return null;
}
